/*
 * Commercial proposals (WORK > Proposals), copied from the WordPress Steward's includes/proposals.php and
 * assets/src/08-proposals.js (3.39.0 to 3.132.0). Built from what OctoClean teaches (the commercial-bids skill).
 *
 * A proposal is a quote with a proposal block, so sending, follow-ups, the viewed note, approving with a signature and
 * turning it into a job all work as for any quote.
 *
 * Price by hours, not square feet: the walkthrough lists the rooms (square feet and what kind of room) and the restroom
 * fixtures; the cleaning speeds turn that into hours a visit; the crew's hourly pay divided by their share of the price
 * gives the price per cleaning hour (crew 40% at $20 an hour is $50); hours x visits a month is the monthly price, to the
 * nearest $5. Three options from three crew rates. The owner keeps what is left after the crew's share and the supplies,
 * flagged when it is under the margin goal. Never below the visit minimum.
 *
 * Construction cleanup is one time, in phases (rough, detail, touch-up), each from the square feet at that phase's speed.
 * Maid service is the hourly rate a visit, with its own minimum.
 *
 * Money is whole cents. Hours and percents are plain numbers; every money answer is rounded to whole cents.
 */
import { z } from "zod";
import type { PublicProposal } from "./proposals.zod.js";

// The kinds of rooms, industries, services, phases and how often -------------------------------------------------

export const PROPOSAL_AREAS = [
  "office",
  "hard",
  "common",
  "conference",
  "kitchen",
  "restroom",
  "exam",
  "gym",
  "classroom",
  "seating",
  "warehouse",
] as const;
export type ProposalArea = (typeof PROPOSAL_AREAS)[number];

export const PROPOSAL_AREA_WORDS: Record<ProposalArea, string> = {
  office: "Offices (carpet)",
  hard: "Rooms with hard floors",
  common: "Lobby, halls and common areas",
  conference: "Conference rooms",
  kitchen: "Break room or kitchen",
  restroom: "Restrooms (floor)",
  exam: "Exam or treatment rooms",
  gym: "Workout floor",
  classroom: "Classrooms",
  seating: "Sanctuary or seating area",
  warehouse: "Warehouse or plant floor",
};

export const PROPOSAL_INDUSTRIES = [
  "office",
  "medical",
  "retail",
  "restaurant",
  "gym",
  "school",
  "church",
  "commons",
  "industrial",
] as const;
export type ProposalIndustry = (typeof PROPOSAL_INDUSTRIES)[number];

export const PROPOSAL_INDUSTRY_WORDS: Record<ProposalIndustry, string> = {
  office: "Office, bank or insurance",
  medical: "Medical, dental or vet",
  retail: "Retail, salon or showroom",
  restaurant: "Restaurant or bar",
  gym: "Gym, studio or club",
  school: "School, day care or library",
  church: "Church or event space",
  commons: "Apartment or hotel common areas",
  industrial: "Warehouse or plant",
};

export const PROPOSAL_SERVICES = ["janitorial", "construction", "maid"] as const;
export type ProposalService = (typeof PROPOSAL_SERVICES)[number];
export const PROPOSAL_SERVICE_WORDS: Record<ProposalService, string> = {
  janitorial: "Janitorial",
  construction: "Construction cleanup",
  maid: "Maid service (hourly)",
};

export const PROPOSAL_PHASES = ["rough", "detail", "touchup"] as const;
export type ProposalPhase = (typeof PROPOSAL_PHASES)[number];
export const PROPOSAL_PHASE_WORDS: Record<ProposalPhase, string> = {
  rough: "Rough clean",
  detail: "Detail clean",
  touchup: "Touch-up",
};

export const PROPOSAL_FIXTURES = ["toilet", "urinal", "sink"] as const;
export type ProposalFixture = (typeof PROPOSAL_FIXTURES)[number];

export const PROPOSAL_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;

export const PROPOSAL_PICKS = ["low", "std", "high", "custom"] as const;
export type ProposalPick = (typeof PROPOSAL_PICKS)[number];

/** How often (the owner's list): key, label, visits a month, words. One-time is one visit, priced once. */
export const PROPOSAL_FREQS = [
  ...[1, 2, 3, 4, 5, 6, 7].map((n) => ({
    key: `w${String(n)}`,
    label: `${String(n)} day${n === 1 ? "" : "s"} per week`,
    perMonth: (n * 52) / 12,
    words: `${String(n)} day${n === 1 ? "" : "s"} a week`,
  })),
  { key: "eow", label: "Every other week", perMonth: 26 / 12, words: "every other week" },
  { key: "monthly", label: "Monthly", perMonth: 1, words: "once a month" },
  { key: "twice", label: "Twice per month", perMonth: 2, words: "twice a month" },
  { key: "once", label: "One-time", perMonth: 1, words: "one visit" },
] as const;
export const PROPOSAL_FREQ_KEYS = PROPOSAL_FREQS.map((f) => f.key);

/** The scope of work's four lists. Construction cleanup uses them for its phases. */
export const PROPOSAL_SCOPE_KEYS = ["daily", "weekly", "monthly", "periodic"] as const;
export type ProposalScopeKey = (typeof PROPOSAL_SCOPE_KEYS)[number];
export type ProposalScope = Record<ProposalScopeKey, string[]>;

export function proposalScopeWords(service: ProposalService, forClient = false): Record<ProposalScopeKey, string> {
  if (service === "construction")
    return {
      daily: "Rough clean",
      weekly: "Detail clean",
      monthly: "Touch-up",
      periodic: forClient ? "Extras, priced separately" : "Extras (priced separately)",
    };
  return forClient
    ? { daily: "Every visit", weekly: "Once a week", monthly: "Once a month", periodic: "When you need it, priced separately" }
    : { daily: "Every visit", weekly: "Weekly", monthly: "Monthly", periodic: "When needed (priced separately)" };
}

// The scope of work for each industry (the old Steward's templates) ----------------------------------------------

const REST = [
  "Clean and disinfect toilets, urinals, sinks and counters",
  "Clean mirrors and wipe partitions where touched",
  "Refill toilet paper, paper towels and hand soap from your stock",
  "Sweep and mop restroom floors",
];
const BASE = [
  "Empty trash and recycling, and replace liners",
  "Wipe door handles, light switches and other high-touch spots",
  "Spot clean glass at the entry doors",
];
const sc = (daily: string[], weekly: string[], monthly: string[], periodic: string[]): ProposalScope => ({
  daily,
  weekly,
  monthly,
  periodic,
});

export const PROPOSAL_SCOPES: Record<ProposalIndustry, ProposalScope> = {
  office: sc(
    [
      ...BASE,
      ...REST,
      "Wipe break room counters, sink, tables and the outside of appliances",
      "Vacuum carpet in walkways and open areas",
      "Sweep and damp mop hard floors",
    ],
    [
      "Dust desks, sills, ledges and furniture",
      "Vacuum carpet edge to edge",
      "Wipe cabinet and appliance fronts",
      "Clean conference room tables and chairs",
    ],
    [
      "Dust vents, high ledges and blinds",
      "Wipe baseboards",
      "Clean inside the microwave",
      "Detail restroom partitions and walls",
    ],
    ["Carpet shampooing", "Hard floor strip and wax", "Interior windows"],
  ),
  medical: sc(
    [
      ...BASE,
      ...REST,
      "Disinfect exam room counters, chairs and handles",
      "Wipe waiting room chairs, tables and the front desk",
      "Sweep and damp mop hard floors",
      "Vacuum carpet",
    ],
    ["Dust ledges, sills and frames", "Wipe cabinet fronts", "Clean waiting room glass"],
    ["Dust vents and high ledges", "Wipe baseboards", "Detail restroom partitions and walls"],
    ["Hard floor strip and wax", "Carpet shampooing"],
  ),
  retail: sc(
    [...BASE, ...REST, "Clean entry doors and glass", "Dust counters and displays", "Sweep and mop the sales floor"],
    ["Dust shelves and fixtures", "Wipe baseboards near the entry"],
    ["Dust vents and high ledges", "Detail restroom partitions and walls"],
    ["Hard floor strip and wax", "Interior windows"],
  ),
  restaurant: sc(
    [...BASE, ...REST, "Wipe tables, chairs and booths", "Sweep and mop the dining room"],
    ["Dust decor, frames and ledges", "Wipe baseboards"],
    ["Dust vents and light fixtures", "Detail restroom partitions and walls"],
    ["Hard floor deep scrub"],
  ),
  gym: sc(
    [
      ...BASE,
      ...REST,
      "Wipe and disinfect equipment touch points and mats",
      "Clean locker rooms and showers",
      "Vacuum or mop the workout floor",
    ],
    ["Clean mirrors edge to edge", "Dust ledges and sills"],
    ["Dust vents and high ledges", "Detail locker room walls and partitions"],
    ["Hard floor deep scrub", "Carpet or turf shampooing"],
  ),
  school: sc(
    [
      ...BASE,
      ...REST,
      "Wipe desks, tables and chairs",
      "Wipe drinking fountains",
      "Sweep and mop halls and hard floors",
      "Vacuum carpet and rugs",
    ],
    ["Dust shelves, sills and ledges", "Clean door glass and entry windows", "Wipe cubbies and lockers"],
    ["Dust vents and high ledges", "Wipe baseboards", "Detail restroom partitions and walls"],
    ["Hard floor strip and wax", "Carpet shampooing", "Summer deep clean"],
  ),
  church: sc(
    [
      ...BASE,
      ...REST,
      "Straighten and wipe pews or seating",
      "Vacuum aisles and carpet",
      "Sweep and mop the lobby and hard floors",
      "Wipe the kitchen or fellowship hall counters",
    ],
    ["Dust rails, ledges and sills", "Clean entry glass and doors"],
    ["Dust vents, light fixtures and high ledges", "Wipe baseboards"],
    ["Carpet shampooing", "Hard floor strip and wax", "Cleaning after an event"],
  ),
  commons: sc(
    [
      ...BASE,
      ...REST,
      "Sweep and mop the lobby, halls and stairwells",
      "Vacuum hallway carpet",
      "Wipe mailboxes, elevator doors and panels",
      "Clean the clubhouse or fitness room",
    ],
    ["Dust rails, ledges and light fixtures", "Clean lobby glass and entry doors"],
    ["Wipe hallway baseboards and doors", "Dust vents and high ledges"],
    ["Hallway carpet shampooing", "Move-out unit cleaning", "Hard floor strip and wax"],
  ),
  industrial: sc(
    [
      ...BASE,
      ...REST,
      "Clean the break room counters, tables and sink",
      "Sweep the office and break areas",
      "Empty trash at workstations",
    ],
    ["Sweep warehouse aisles", "Dust office desks and ledges"],
    ["Dust vents and high ledges", "Wipe baseboards and doors"],
    ["Warehouse floor scrubbing", "Hard floor strip and wax", "High dusting"],
  ),
};

export const PROPOSAL_CONSTRUCTION_SCOPE: ProposalScope = sc(
  [
    "Haul out construction debris, boxes and trash",
    "Sweep and vacuum every floor",
    "Remove stickers, tape and labels",
    "Wipe down sills, ledges and counters",
    "Clear dust from vents and light fixtures",
  ],
  [
    "Clean windows inside, with frames and tracks",
    "Wipe cabinets and drawers inside and out",
    "Clean and polish restrooms, fixtures and mirrors",
    "Wipe doors, frames, switches and baseboards",
    "Clean appliances inside and out",
    "Vacuum and mop every floor",
  ],
  [
    "Dust surfaces after the last trades leave",
    "Spot clean walls, glass and fingerprints",
    "Final vacuum and mop",
    "Walk through with the owner or builder",
  ],
  ["Exterior windows", "Floor scrubbing or waxing", "Pressure washing walks and entries"],
);

/** The scope a new proposal starts from. */
export function proposalScopeFor(service: ProposalService, industry: ProposalIndustry): ProposalScope {
  const src = service === "construction" ? PROPOSAL_CONSTRUCTION_SCOPE : PROPOSAL_SCOPES[industry];
  return { daily: [...src.daily], weekly: [...src.weekly], monthly: [...src.monthly], periodic: [...src.periodic] };
}

/**
 * A typical layout for each industry (CleanGuru's Auto-Load percentages): room, kind of room, percent of the building.
 * "Total size" on the walkthrough splits the square feet this way; every row can be changed after.
 */
export const PROPOSAL_MIXES: Record<ProposalIndustry, [string, ProposalArea, number][]> = {
  office: [
    ["Entrances", "common", 1],
    ["Lobbies", "common", 2],
    ["Conference rooms", "conference", 9],
    ["Offices", "office", 61],
    ["Halls", "common", 16],
    ["Restrooms", "restroom", 4],
    ["Break rooms", "kitchen", 5],
    ["Utility rooms", "hard", 2],
  ],
  medical: [
    ["Waiting area", "common", 8],
    ["Reception and nurses stations", "office", 5],
    ["Exam rooms", "exam", 34],
    ["Lab", "exam", 5],
    ["Offices", "office", 19],
    ["Halls", "common", 15],
    ["Restrooms", "restroom", 4],
    ["Break room", "kitchen", 5],
    ["Utility rooms", "hard", 1],
    ["Conference rooms", "conference", 4],
  ],
  retail: [
    ["Entrances", "common", 1],
    ["Sales floor", "hard", 76],
    ["Checkout", "hard", 5],
    ["Stock rooms", "hard", 11],
    ["Break room", "kitchen", 2],
    ["Restrooms", "restroom", 3],
    ["Loading dock", "warehouse", 1],
    ["Offices", "office", 1],
  ],
  restaurant: [
    ["Entrances", "common", 2],
    ["Waiting area", "common", 4],
    ["Coat room", "common", 1],
    ["Bar", "hard", 7],
    ["Dining room", "hard", 42],
    ["Kitchen", "kitchen", 26],
    ["Halls", "common", 3],
    ["Restrooms", "restroom", 5],
    ["Office", "office", 1],
    ["Loading dock", "warehouse", 4],
    ["Storage", "hard", 5],
  ],
  gym: [
    ["Entrances", "common", 1],
    ["Check-in", "common", 2],
    ["Group class room", "gym", 10],
    ["Equipment floor", "gym", 38],
    ["Pool area", "hard", 21],
    ["Locker rooms", "restroom", 14],
    ["Restrooms", "restroom", 5],
    ["Kids room", "office", 2],
    ["Offices", "office", 2],
    ["Halls", "common", 3],
    ["Storage", "hard", 2],
  ],
  school: [
    ["Entrances", "common", 1],
    ["Halls", "common", 13],
    ["Offices", "office", 4],
    ["Meeting rooms", "conference", 2],
    ["Classrooms", "classroom", 35],
    ["Library", "office", 4],
    ["Music rooms", "classroom", 2],
    ["Cafeteria", "hard", 10],
    ["Kitchen", "kitchen", 2],
    ["Art rooms", "classroom", 4],
    ["Staff lounge", "kitchen", 1],
    ["Restrooms", "restroom", 3],
    ["Locker rooms", "restroom", 1],
    ["Utility rooms", "hard", 8],
    ["Gym", "hard", 10],
  ],
  church: [
    ["Entrances", "common", 2],
    ["Lobbies", "common", 14],
    ["Sanctuary", "seating", 45],
    ["Meeting and activity rooms", "hard", 24],
    ["Cry room", "office", 2],
    ["Choir room", "classroom", 4],
    ["Restrooms", "restroom", 6],
    ["Utility rooms", "hard", 3],
  ],
  commons: [
    ["Halls", "common", 85],
    ["Lobbies", "common", 7],
    ["Laundry room", "hard", 8],
  ],
  industrial: [
    ["Warehouse floor", "warehouse", 96],
    ["Restrooms", "restroom", 4],
  ],
};

// Settings (Settings section "proposals"; each brand or location can change any part) ------------------------------

const cents = (max: number) => z.number().int().min(0).max(max);
const lineList = (max: number) => z.array(z.string().trim().min(1).max(200)).max(max);
const areaRates = z.object(
  Object.fromEntries(PROPOSAL_AREAS.map((k) => [k, z.number().int().min(100).max(20_000)])) as Record<ProposalArea, z.ZodNumber>,
);
const crewRates = z.object({ low: cents(50_000).min(100), std: cents(50_000).min(100), high: cents(50_000).min(100) });
const fixtureMinutes = z.object({
  toilet: z.number().min(0).max(60),
  urinal: z.number().min(0).max(60),
  sink: z.number().min(0).max(60),
});
const constructionRates = z.object({
  rough: z.number().int().min(20).max(5000),
  detail: z.number().int().min(20).max(5000),
  touchup: z.number().int().min(20).max(5000),
});

export const proposalSettings = z.object({
  /** The least a visit costs, every visit whatever the frequency; construction cleanup as a whole. */
  minVisitCents: cents(1_000_000),
  /** The crew's share of the price, percent. */
  sharePercent: z.number().min(10).max(90),
  /** Cleaning supplies, percent of the price (an estimate). */
  supplyPercent: z.number().min(0).max(50),
  /** What the owner should keep, percent. Under it the price shows a warning. */
  marginGoal: z.number().min(0).max(90),
  /** Extra time for setup, percent of the hours. */
  bufferPercent: z.number().min(0).max(100),
  /** What the crew makes an hour for the three options. */
  crewCents: crewRates,
  /** Square feet one person cleans in an hour, for each kind of room. */
  areaRates,
  /** Minutes to clean each restroom fixture. */
  fixtureMinutes,
  /** Construction cleanup: square feet an hour for each phase. */
  construction: constructionRates,
  /** Maid service: the price an hour and the least a visit costs. 0 means not set. */
  maidHourCents: cents(100_000),
  maidMinCents: cents(1_000_000),
  /** The letter at the top of every proposal, and who signs it. */
  intro: z.string().max(3000),
  signName: z.string().max(60),
  signTitle: z.string().max(60),
  /** The rating as it is on Google today (only the real number), and up to 6 reviews as "what they said | their name". */
  rating: z.string().max(80),
  reviews: z.array(z.string().trim().min(1).max(600)).max(6),
  highlights: lineList(12),
  bring: lineList(12),
  insurance: z.string().max(3000),
  quality: z.string().max(3000),
  /** One term a line. */
  terms: z.string().max(3000),
});
export type ProposalSettings = z.infer<typeof proposalSettings>;

/** What a company may store: any part, and any part of the rate groups. */
export const proposalSettingsPatch = proposalSettings
  .extend({
    crewCents: crewRates.partial().strict(),
    areaRates: areaRates.partial().strict(),
    fixtureMinutes: fixtureMinutes.partial().strict(),
    construction: constructionRates.partial().strict(),
  })
  .partial()
  .strict();

export const PROPOSAL_SETTINGS_DEFAULTS: ProposalSettings = {
  minVisitCents: 10_000,
  sharePercent: 40,
  supplyPercent: 5,
  marginGoal: 40,
  bufferPercent: 10,
  crewCents: { low: 1800, std: 2000, high: 2400 },
  // Starting points, to be replaced with the crew's own times.
  areaRates: {
    office: 3000,
    hard: 2500,
    common: 2500,
    conference: 3000,
    kitchen: 1500,
    restroom: 1500,
    exam: 1500,
    gym: 2500,
    classroom: 2500,
    seating: 3500,
    warehouse: 6000,
  },
  fixtureMinutes: { toilet: 3, urinal: 2, sink: 2 },
  // From CleanGuru's construction prices, worked back to hours.
  construction: { rough: 425, detail: 140, touchup: 575 },
  maidHourCents: 0,
  maidMinCents: 0,
  intro:
    "Thank you for giving us the opportunity to care for your building. We help businesses like yours stay clean, polished and ready for customers, removing the dust, debris and buildup that daily traffic leaves behind.\n\nWe are a licensed and insured cleaning company, known for being professional, reliable and careful with every space we clean. If you have any questions, call or text us anytime. We look forward to working with you.",
  signName: "",
  signTitle: "Owner",
  rating: "",
  reviews: [],
  highlights: [
    "Background-checked, bonded and insured team",
    "The same crew at your building each visit",
    "A written checklist for every visit",
    "We bring our own cleaning supplies and equipment",
    "After-hours or early-morning cleaning around your schedule",
    "One person to text who answers",
  ],
  bring: [
    "All cleaning supplies and equipment",
    "Professional vacuums",
    "Disinfectants, cleaning sprays and microfiber cloths",
    "Tools for glass, mirrors and detailed surfaces",
  ],
  insurance:
    "We carry $2,000,000 in general liability insurance and full workers' compensation, so everyone is protected. A certificate of insurance is available on request.",
  quality:
    "Your crew works from a written checklist for your building. You can text us anytime, and if anything is missed we come back and make it right.",
  terms:
    "Billed monthly. Invoices are due within 7 days.\nEither of us can end the service with 30 days' written notice.\nRestroom paper, hand soap and trash liners are supplied by you unless we agree otherwise.\nPrices are reviewed once a year.",
};

// The walkthrough --------------------------------------------------------------------------------------------------

export const proposalRoom = z.object({
  name: z.string().trim().max(60).default(""),
  kind: z.enum(PROPOSAL_AREAS),
  sqft: z.number().int().min(0).max(500_000),
  count: z.number().int().min(1).max(200).default(1),
});
export type ProposalRoom = z.infer<typeof proposalRoom>;

/**
 * A line item priced on its own (owner, 2026-11-03, a church: hall and restrooms twice a month, the office once a
 * month, the sanctuary as an add-on): what part of the building, how often, the price a visit, whether the client may
 * leave it out, and what is included, listed under the line on the proposal.
 */
export const proposalPart = z.object({
  name: z.string().trim().max(80).default(""),
  freq: z.enum(PROPOSAL_FREQ_KEYS as [string, ...string[]]).default("twice"),
  visitCents: cents(10_000_000).default(0),
  optional: z.boolean().default(false),
  included: lineList(30).default([]),
});
export type ProposalPart = z.infer<typeof proposalPart>;

/** What the office types: the building, the walkthrough, the pick and the words. */
export const proposalWalk = z.object({
  industry: z.enum(PROPOSAL_INDUSTRIES).default("office"),
  service: z.enum(PROPOSAL_SERVICES).default("janitorial"),
  /** The building's total square feet, split by the industry's mix when typed. */
  totalSqft: z.number().int().min(0).max(5_000_000).nullable().default(null),
  rooms: z.array(proposalRoom).max(80).default([]),
  fixtures: z
    .object({
      toilet: z.number().int().min(0).max(500),
      urinal: z.number().int().min(0).max(500),
      sink: z.number().int().min(0).max(500),
    })
    .default({ toilet: 0, urinal: 0, sink: 0 }),
  phases: z
    .array(z.enum(PROPOSAL_PHASES))
    .max(3)
    .default([...PROPOSAL_PHASES]),
  freq: z.enum(PROPOSAL_FREQ_KEYS as [string, ...string[]]).default("w5"),
  days: z.array(z.enum(PROPOSAL_DAYS)).max(7).default([]),
  time: z.string().trim().max(60).default(""),
  pick: z.enum(PROPOSAL_PICKS).default("std"),
  /** Your own price, a month (or one time). 0 for none. */
  customCents: cents(100_000_000).default(0),
  /** Restroom paper, hand soap and trash liners: the client's or ours, and our monthly price for them (0 included). */
  supplies: z.enum(["client", "us"]).default("client"),
  suppliesCents: cents(10_000_000).default(0),
  about: z.string().trim().max(2000).default(""),
  /** Line items priced on their own. Any with a name and a price set the proposal's price instead of the pick. */
  parts: z.array(proposalPart).max(20).default([]),
  scope: z
    .object(
      Object.fromEntries(PROPOSAL_SCOPE_KEYS.map((k) => [k, lineList(40)])) as Record<
        ProposalScopeKey,
        ReturnType<typeof lineList>
      >,
    )
    .nullable()
    .default(null),
});
export type ProposalWalk = z.infer<typeof proposalWalk>;

// The price --------------------------------------------------------------------------------------------------------

export interface ProposalOption {
  /** The price a month, or one time. */
  monthlyCents: number;
  /** What one visit comes to. */
  perVisitCents: number;
  /** The price per cleaning hour. */
  rateCents: number;
  /** What the crew makes an hour at this price. */
  crewHourCents: number;
  crewCents: number;
  suppliesCents: number;
  keepCents: number;
  /** Raised to the visit minimum. */
  atMin: boolean;
}

export interface ProposalCalc {
  service: ProposalService;
  /** Hours a visit (all phases for construction), to the quarter hour. */
  hours: number;
  /** Construction: hours for each picked phase. */
  phases: Partial<Record<ProposalPhase, number>> | null;
  /** Maid service with no hourly rate set. */
  noRate: boolean;
  freq: string;
  once: boolean;
  visitsPerMonth: number;
  words: string;
  options: Partial<Record<ProposalPick, ProposalOption>>;
  keepPercent: number;
  marginOk: boolean;
}

export function proposalFreq(w: Pick<ProposalWalk, "service" | "freq">) {
  if (w.service === "construction") return PROPOSAL_FREQS[PROPOSAL_FREQS.length - 1]!;
  return PROPOSAL_FREQS.find((f) => f.key === w.freq) ?? PROPOSAL_FREQS[4]!;
}

/** Square feet of every room, times how many like it. */
export function proposalSqft(rooms: readonly ProposalRoom[]): number {
  return rooms.reduce((t, r) => t + Math.max(0, r.sqft) * Math.max(1, r.count), 0);
}

const quarter = (h: number) => Math.ceil(h * 4) / 4;
const to5 = (c: number) => Math.round(c / 500) * 500;

export function proposalCalc(w: ProposalWalk, s: ProposalSettings): ProposalCalc {
  const share = s.sharePercent / 100;
  const supply = s.supplyPercent / 100;
  const fq = proposalFreq(w);
  const perMonth = fq.perMonth;
  const minV = Math.max(0, s.minVisitCents);
  const split = (o: Omit<ProposalOption, "monthlyCents" | "crewCents" | "suppliesCents" | "keepCents">, monthly: number) => {
    const crew = Math.round(monthly * share);
    const supplies = Math.round(monthly * supply);
    return { ...o, monthlyCents: monthly, crewCents: crew, suppliesCents: supplies, keepCents: monthly - crew - supplies };
  };
  const options: Partial<Record<ProposalPick, ProposalOption>> = {};
  let hours = 0;
  let phases: Partial<Record<ProposalPhase, number>> | null = null;
  let noRate = false;
  if (w.service === "construction") {
    const sq = proposalSqft(w.rooms);
    const picked = w.phases.length ? w.phases : [...PROPOSAL_PHASES];
    phases = {};
    for (const k of PROPOSAL_PHASES) if (picked.includes(k) && sq > 0) phases[k] = quarter(sq / Math.max(1, s.construction[k]));
    hours = Object.values(phases).reduce((a, b) => a + b, 0);
    for (const k of ["low", "std", "high"] as const) {
      const rate = Math.round(s.crewCents[k] / share);
      let total = 0;
      for (const h of Object.values(phases)) total += to5(h * rate);
      const atMin = minV > 0 && total > 0 && total < minV;
      if (atMin) total = minV;
      options[k] = split(
        {
          rateCents: rate,
          perVisitCents: total,
          crewHourCents: atMin && hours ? Math.round((minV * share) / hours) : s.crewCents[k],
          atMin,
        },
        total,
      );
    }
  } else {
    let minutes = 0;
    for (const r of w.rooms) {
      const sq = Math.max(0, r.sqft) * Math.max(1, r.count);
      if (sq > 0) minutes += (sq / Math.max(1, s.areaRates[r.kind])) * 60;
    }
    for (const f of PROPOSAL_FIXTURES) minutes += Math.max(0, w.fixtures[f]) * s.fixtureMinutes[f];
    hours = minutes > 0 ? quarter((minutes / 60) * (1 + s.bufferPercent / 100)) : 0;
    if (w.service === "maid") {
      if (s.maidHourCents <= 0) noRate = true;
      else if (hours > 0) {
        const rate = s.maidHourCents;
        const visit = Math.max(s.maidMinCents, Math.round(hours * rate));
        options.std = split(
          { rateCents: rate, perVisitCents: visit, crewHourCents: Math.round(rate * share), atMin: visit > hours * rate },
          Math.round(visit * perMonth),
        );
      }
    } else if (hours > 0) {
      for (const k of ["low", "std", "high"] as const) {
        const rate = Math.round(s.crewCents[k] / share);
        const visit = hours * rate;
        const atMin = minV > 0 && visit < minV;
        const perVisit = Math.round(atMin ? minV : visit);
        // At the minimum the crew's share is more an hour.
        options[k] = split(
          {
            rateCents: rate,
            perVisitCents: perVisit,
            crewHourCents: atMin ? Math.round((minV * share) / hours) : s.crewCents[k],
            atMin,
          },
          to5((atMin ? minV : visit) * perMonth),
        );
      }
    }
  }
  // Your own price (like CleanGuru's "Enter your price"): the same split, and what it means an hour.
  const custom = Math.max(0, Math.round(w.customCents));
  if (custom > 0 && hours > 0) {
    const hm = hours * perMonth;
    options.custom = split(
      {
        rateCents: Math.round(custom / hm),
        perVisitCents: Math.round(custom / perMonth),
        crewHourCents: Math.round((custom * share) / hm),
        atMin: false,
      },
      custom,
    );
  }
  const keepPercent = Math.round((1 - share - supply) * 1000) / 10;
  return {
    service: w.service,
    hours,
    phases,
    noRate,
    freq: fq.key,
    once: fq.key === "once",
    visitsPerMonth: Math.round(perMonth * 100) / 100,
    words: fq.words,
    options,
    keepPercent,
    marginOk: keepPercent >= s.marginGoal - 0.001,
  };
}

/** The option the proposal uses: the one picked, else standard, else the first there is. */
export function proposalPicked(w: ProposalWalk, c: ProposalCalc): { pick: ProposalPick; option: ProposalOption } | null {
  const pick = c.options[w.pick] ? w.pick : c.options.std ? "std" : (Object.keys(c.options)[0] as ProposalPick | undefined);
  const option = pick ? c.options[pick] : undefined;
  return pick && option ? { pick, option } : null;
}

/** The line items that count: a name and a price. */
export function proposalPricedParts(w: Pick<ProposalWalk, "service" | "parts">): ProposalPart[] {
  if (w.service === "construction") return [];
  return w.parts.filter((p) => p.name.trim() && p.visitCents > 0);
}

/** A line item a month (one visit for one time): the price a visit times the visits a month. */
export function proposalPartCents(p: Pick<ProposalPart, "freq" | "visitCents">): number {
  const f = PROPOSAL_FREQS.find((x) => x.key === p.freq) ?? PROPOSAL_FREQS[9]!;
  return f.key === "once" ? p.visitCents : Math.round(p.visitCents * f.perMonth);
}

/** The line items' price, without the ones the client may leave out. */
export function proposalPartsTotal(w: Pick<ProposalWalk, "service" | "parts">): number {
  return proposalPricedParts(w)
    .filter((p) => !p.optional)
    .reduce((t, p) => t + proposalPartCents(p), 0);
}

/** Every priced line item is one visit, so the won proposal is a one-time job. */
export function proposalPartsOnce(w: Pick<ProposalWalk, "service" | "parts">): boolean {
  const parts = proposalPricedParts(w);
  return parts.length > 0 && parts.every((p) => p.freq === "once");
}

/**
 * Total size (CleanGuru's Auto-Load): the building's square feet split like a typical building of its industry. The
 * restroom counts are guessed too, one toilet and one sink for every 2,500 square feet, a urinal for every 3 toilets.
 */
export function proposalSplit(industry: ProposalIndustry, totalSqft: number) {
  const total = Math.round(totalSqft);
  if (total <= 0) return null;
  const rooms: ProposalRoom[] = PROPOSAL_MIXES[industry].map(([name, kind, pct]) => ({
    name,
    kind,
    sqft: Math.round((total * pct) / 100),
    count: 1,
  }));
  const t = Math.max(1, Math.round(total / 2500));
  return { rooms, fixtures: { toilet: t, urinal: Math.floor(t / 3), sink: t } };
}

// What the client reads --------------------------------------------------------------------------------------------

export function proposalTitle(service: ProposalService, freq: string, building: string): string {
  if (service === "construction") return `Construction cleanup for ${building}`;
  if (service === "maid") return `Maid service for ${building}`;
  return `${freq === "once" ? "One-time cleaning for" : "Janitorial service for"} ${building}`;
}

/** When it happens, in words: the days, else how often. */
export function proposalWhen(w: Pick<ProposalWalk, "service" | "freq" | "days" | "time" | "parts">): string {
  const parts = proposalPricedParts(w);
  if (parts.length) {
    const each = parts.map((p) => `${p.name.trim()} ${proposalFreq({ service: w.service, freq: p.freq }).words}`).join(", ");
    const out = w.days.length ? `${each}. On ${w.days.join(", ")}` : each;
    return w.time ? `${out}, ${w.time}` : out;
  }
  const f = proposalFreq(w);
  const days = w.days.join(", ");
  let out: string;
  if (f.key === "once") out = `One visit${days ? `, ${days}` : ""}`;
  else if (days && f.key.startsWith("w")) out = days;
  else out = days ? `${days}, ${f.words}` : f.words.charAt(0).toUpperCase() + f.words.slice(1);
  return w.time ? `${out}, ${w.time}` : out;
}

export interface ProposalLine {
  description: string;
  note: string;
  amountCents: number;
  /** The client may leave it out (a line item marked optional). */
  optional?: boolean;
}

/** The supplies line when we bring them for a price. */
function suppliesLine(w: Pick<ProposalWalk, "supplies" | "suppliesCents">, once: boolean): ProposalLine[] {
  if (w.supplies !== "us" || w.suppliesCents <= 0) return [];
  return [
    {
      description: "Restroom paper, hand soap and trash liners",
      note: once ? "For the visit" : "Kept stocked, billed monthly",
      amountCents: w.suppliesCents,
    },
  ];
}

/** The quote's lines from the line items: one a part, a month, with the price a visit in the note. */
export function proposalPartLines(w: ProposalWalk): ProposalLine[] {
  const lines: ProposalLine[] = proposalPricedParts(w).map((p) => {
    const f = proposalFreq({ service: w.service, freq: p.freq });
    const visit = `$${(p.visitCents / 100).toLocaleString("en-US", { minimumFractionDigits: p.visitCents % 100 ? 2 : 0 })}`;
    const word = f.words.charAt(0).toUpperCase() + f.words.slice(1);
    return {
      description: p.name.trim(),
      note: f.key === "once" ? "One visit" : `${word} at ${visit} a visit, billed monthly`,
      amountCents: proposalPartCents(p),
      optional: p.optional,
    };
  });
  return [...lines, ...suppliesLine(w, proposalPartsOnce(w))];
}

/** The quote's lines, by service. Raised to the minimum, the lines still add up to the price shown. */
export function proposalLines(w: ProposalWalk, c: ProposalCalc, pick: ProposalPick, o: ProposalOption): ProposalLine[] {
  const once = c.once;
  const days = w.service !== "construction" && !once && w.days.length ? `, ${w.days.join(", ")}` : "";
  if (w.service === "construction") {
    const ph = PROPOSAL_PHASES.filter((k) => c.phases?.[k] !== undefined);
    if (pick === "custom")
      return [
        {
          description: `Construction cleanup, ${ph.map((k) => PROPOSAL_PHASE_WORDS[k].toLowerCase()).join(", ")}`,
          note: "One time",
          amountCents: o.monthlyCents,
        },
      ];
    const lines = ph.map((k) => ({
      description: `${PROPOSAL_PHASE_WORDS[k]} after construction`,
      note: `About ${String(c.phases?.[k] ?? 0)} hours`,
      amountCents: to5((c.phases?.[k] ?? 0) * o.rateCents),
    }));
    const sum = lines.reduce((t, l) => t + l.amountCents, 0);
    if (o.atMin && lines.length && o.monthlyCents > sum)
      lines.push({ description: "Minimum visit", note: "The least a visit costs", amountCents: o.monthlyCents - sum });
    return lines;
  }
  const what =
    w.service === "maid"
      ? `Maid service, about ${String(c.hours)} hours a visit`
      : once
        ? "One-time cleaning"
        : "Janitorial service";
  const word = c.words.charAt(0).toUpperCase() + c.words.slice(1);
  return [
    { description: what + days, note: once ? "One visit" : `${word}, billed monthly`, amountCents: o.monthlyCents },
    ...suppliesLine(w, once),
  ];
}

/** The terms, one a line. When we supply the restroom paper, the line that says the client does reads the other way. */
export function proposalTerms(terms: string, w: Pick<ProposalWalk, "supplies" | "suppliesCents">): string[] {
  const list = terms
    .split("\n")
    .map((t) => t.trim())
    .filter(Boolean);
  if (w.supplies !== "us") return list;
  return list.map((t) =>
    t.toLowerCase().includes("supplied by you")
      ? `We supply restroom paper, hand soap and trash liners and keep them stocked${w.suppliesCents > 0 ? ", billed monthly with the service." : ", included in the price."}`
      : t,
  );
}

/** The job a won proposal becomes: billed monthly as one monthly job, one time as a one-time job. */
export function proposalJobFrequency(c: Pick<ProposalCalc, "once">): string {
  return c.once ? "one-time" : "monthly";
}

/** The words saved with a proposal when it is saved, so a later settings change never rewrites a sent one. */
export const PROPOSAL_WORD_KEYS = [
  "intro",
  "signName",
  "signTitle",
  "highlights",
  "bring",
  "insurance",
  "quality",
  "terms",
] as const;
export type ProposalWords = Pick<ProposalSettings, (typeof PROPOSAL_WORD_KEYS)[number]>;

export function proposalWordsOf(s: ProposalSettings): ProposalWords {
  return {
    intro: s.intro,
    signName: s.signName,
    signTitle: s.signTitle,
    highlights: [...s.highlights],
    bring: [...s.bring],
    insurance: s.insurance,
    quality: s.quality,
    terms: s.terms,
  };
}

/**
 * The client's page, in the owner's own proposal layout: a cover, a short letter signed by the owner, the proposal at a
 * glance, their building, the scope as a checklist, what we bring, insurance, why us, how we keep it right and numbered
 * terms. The rating and reviews are today's (only the real number on Google).
 */
export function proposalPublic(p: {
  walk: ProposalWalk;
  words: ProposalWords;
  building: string;
  contactName: string | null;
  address: string | null;
  brand: string;
  /** "October 29, 2026". */
  date: string;
  rating: string;
  reviews: readonly string[];
}): PublicProposal {
  const w = p.walk;
  const who = (p.contactName ?? "").trim();
  const first = who && who.toLowerCase() !== p.building.toLowerCase() ? (who.split(/\s+/)[0] ?? "") : "";
  const labels = proposalScopeWords(w.service, true);
  const ours = w.supplies === "us";
  const scope = PROPOSAL_SCOPE_KEYS.map((k) => ({
    label: labels[k],
    items: (w.scope?.[k] ?? []).map((x) => (ours ? x.replace("from your stock", "from our stock") : x)),
  })).filter((x) => x.items.length);
  const bring = [...p.words.bring];
  if (ours)
    bring.push(
      `Restroom paper, hand soap and trash liners, kept stocked${w.suppliesCents > 0 ? "" : ", included in your price"}`,
    );
  const included = proposalPricedParts(w)
    .filter((x) => x.included.length)
    .map((x) => ({ line: x.name.trim(), items: [...x.included] }));
  const brandShort = p.brand.replace(/^The\s+/i, "");
  return {
    serviceWords:
      w.service === "construction" ? "Construction cleanup" : w.service === "maid" ? "Maid service" : "Janitorial service",
    building: p.building,
    preparedFor: who || p.building,
    address: p.address,
    date: p.date,
    greeting: first ? `Hello ${first},` : "Hello,",
    letter: p.words.intro
      .split(/\n/)
      .map((x) => x.trim())
      .filter(Boolean),
    signName: p.words.signName.trim() || `The ${brandShort} team`,
    signTitle: p.words.signName.trim() && p.words.signTitle.trim() ? `${p.words.signTitle.trim()}, ${p.brand}` : "",
    when: proposalWhen(w),
    once: proposalPricedParts(w).length ? proposalPartsOnce(w) : proposalFreq(w).key === "once",
    about: w.about,
    included,
    scope,
    bring,
    insurance: p.words.insurance.trim(),
    highlights: [...p.words.highlights],
    quality: p.words.quality.trim(),
    terms: proposalTerms(p.words.terms, w),
    rating: p.rating.trim(),
    reviews: p.reviews.map((r) => {
      const [text = "", name = ""] = r.split("|", 2).map((x) => x.trim());
      return { text, name };
    }),
  };
}
